/**
 * Invoice Card Component
 * Displays invoice information in a card format
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Invoice } from '../../types/payment';
import { InvoiceStatus } from '../../types/payment';
import { formatCurrency, formatDate } from '../../utils/formatting';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { FileText, Download, Eye } from 'lucide-react';

export interface InvoiceCardProps {
  invoice: Invoice;
  onClick?: () => void;
  onDownload?: () => void;
  onView?: () => void;
  showActions?: boolean;
}

export const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoice,
  onClick,
  onDownload,
  onView,
  showActions = true,
}) => {
  const { t } = useTranslation();

  const getInvoiceStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.PAID:
        return 'border-green-300 bg-green-50';
      case InvoiceStatus.OVERDUE:
        return 'border-red-300 bg-red-50';
      case InvoiceStatus.DRAFT:
        return 'border-gray-300 bg-gray-50';
      case InvoiceStatus.SENT:
      case InvoiceStatus.ISSUED:
        return 'border-blue-300 bg-blue-50';
      case InvoiceStatus.CANCELLED:
        return 'border-gray-400 bg-gray-100';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  const getInvoiceStatusBadge = (status: InvoiceStatus) => {
    const colors = {
      [InvoiceStatus.DRAFT]: 'bg-gray-100 text-gray-800',
      [InvoiceStatus.ISSUED]: 'bg-blue-100 text-blue-800',
      [InvoiceStatus.SENT]: 'bg-indigo-100 text-indigo-800',
      [InvoiceStatus.PAID]: 'bg-green-100 text-green-800',
      [InvoiceStatus.OVERDUE]: 'bg-red-100 text-red-800',
      [InvoiceStatus.CANCELLED]: 'bg-gray-200 text-gray-600',
      [InvoiceStatus.REFUNDED]: 'bg-orange-100 text-orange-800',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {t(`payments:invoiceStatus.${status.toLowerCase()}`)}
      </span>
    );
  };

  const isOverdue =
    invoice.status !== InvoiceStatus.PAID &&
    invoice.status !== InvoiceStatus.CANCELLED &&
    new Date(invoice.dueDate) < new Date();

  const isClickable = Boolean(onClick);

  return (
    <div
      className={`border rounded-lg p-4 ${getInvoiceStatusColor(
        isOverdue ? InvoiceStatus.OVERDUE : invoice.status
      )} ${isClickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{invoice.invoiceNumber}</h3>
            <p className="text-sm text-gray-600">
              {t('payments:invoice.billTo')}: {invoice.billTo.name}
            </p>
          </div>
        </div>
        <div className="text-right">
          {getInvoiceStatusBadge(isOverdue ? InvoiceStatus.OVERDUE : invoice.status)}
        </div>
      </div>

      <div className="mb-3">
        <div className="text-2xl font-bold text-gray-900">
          {formatCurrency(invoice.totalAmount, invoice.currency)}
        </div>
        <div className="text-sm text-gray-600">
          {invoice.items.length} {t('payments:payment.items')}
        </div>
      </div>

      <div className="space-y-1 text-sm mb-3">
        <div className="flex justify-between">
          <span className="text-gray-600">{t('payments:payment.issueDate')}:</span>
          <span>{formatDate(invoice.issueDate)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('payments:payment.dueDate')}:</span>
          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
            {formatDate(invoice.dueDate)}
          </span>
        </div>
        {invoice.paidDate && (
          <div className="flex justify-between">
            <span className="text-gray-600">{t('payments:payment.paidDate')}:</span>
            <span className="text-green-600">{formatDate(invoice.paidDate)}</span>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
        <PaymentStatusBadge status={invoice.paymentStatus} />

        {showActions && (
          <div className="flex gap-2">
            {onView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView();
                }}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                title={t('common:actions.view')}
              >
                <Eye className="w-4 h-4" />
              </button>
            )}
            {onDownload && invoice.invoiceUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload();
                }}
                className="p-2 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                title={t('common:actions.download')}
              >
                <Download className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
